import { useState, useEffect, useCallback, useRef } from 'react';
import { useCashback } from '@/services/cashback.service';

type CashbackTransactionTabProps = {
    closeModal: () => void;
};

type RedemptionItem = {
    id: string;
    amount: number;
    status: string;
    createdAt: string;
    completedAt: string | null;
    ledgerTransactionId: string | null;
};

export const CashbackTransactionTab = ({ }: CashbackTransactionTabProps) => {
    const [redemptions, setRedemptions] = useState<RedemptionItem[]>([]);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const { getCashbackRedemptions } = useCashback();
    const observerRef = useRef<IntersectionObserver | null>(null);
    const lastElementRef = useRef<HTMLDivElement | null>(null);



    // Fetch redemptions
    const fetchRedemptions = useCallback(async (pageNum: number) => {
        if (isLoading) return;

        setIsLoading(true);
        const { data, succeeded } = await getCashbackRedemptions(pageNum, 10);

        if (succeeded && data) {
            setRedemptions(prev => pageNum === 1 ? data.items : [...prev, ...data.items]);
            setHasMore(pageNum < data.totalPages);
        }

        setIsLoading(false);
    }, [getCashbackRedemptions, isLoading]);

    // Initial load
    useEffect(() => {
        fetchRedemptions(1);
    }, []);

    // Infinite scroll
    useEffect(() => {
        if (isLoading) return;

        if (observerRef.current) observerRef.current.disconnect();

        observerRef.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prev => prev + 1);
            }
        });

        if (lastElementRef.current) {
            observerRef.current.observe(lastElementRef.current);
        }

        return () => {
            if (observerRef.current) observerRef.current.disconnect();
        };
    }, [isLoading, hasMore]);

    // Load more when page changes
    useEffect(() => {
        if (page > 1) {
            fetchRedemptions(page);
        }
    }, [page]);

    // Format date as [time] Nov 19, 2025
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const time = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
        const formattedDate = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
        return `${time} ${formattedDate}`;
    };

    return (
        <div className='w-full flex flex-col gap-[1.2rem]'>
            {redemptions.map((transaction, index) => {
                const isLast = index === redemptions.length - 1;
                return (
                    <div
                        key={transaction.id}
                        ref={isLast ? lastElementRef : null}
                        className="flex justify-between px-[2rem] py-[0.55rem] items-center dark:bg-[#191919] bg-[#F5F5F5] rounded-[0.7rem]"
                    >
                        <div>
                            <p className="text-[1.6rem] dark:text-[#F6F6F6] text-[#000000]">Transaction Account</p>
                            <p className="text-[1.3rem] dark:text-[#929292] mt-[0.4rem]">{formatDate(transaction.createdAt)}</p>
                        </div>

                        <p className="text-[1.8rem] text-[#DC483A]">-{transaction.amount}</p>
                    </div>
                );
            })}

            {isLoading && (
                <div className='py-[2rem] text-center'>
                    <div className='flex justify-center items-center gap-[1rem]'>
                        <div className='w-[2rem] h-[2rem] border-[0.2rem] border-[#57E8BA] border-t-transparent rounded-full animate-spin'></div>
                        <span className='text-[1.3rem] dark:text-[#929292] text-[#717171]'>Loading...</span>
                    </div>
                </div>
            )}

            {redemptions.length === 0 && !isLoading && (
                <div className='py-[4rem] text-center'>
                    <p className='text-[1.4rem] dark:text-[#929292] text-[#717171]'>No redemptions found</p>
                </div>
            )}
        </div>
    );
};
